import {
  HOME_POPUP_MALLS,
  type HomePopup,
  type HomePopupMall,
  type HomePopupPayload,
  type PopupDevice,
  POPUP_DEVICES,
} from '../types/homePopup';

/**
 * 홈 팝업 폼 ↔ 와이어 변환. 페이지에서 분리해 둔 이유는 여기 규칙들이 화면보다 계약에 가깝고
 * 조용히 틀리기 쉽기 때문이다(전체 노출의 null 표현, 기간 경계, 음수 기하값 등).
 */

export interface HomePopupFormState {
  popupKey: string;
  title: string;
  contentHtml: string;
  linkUrl: string;
  /** 빈 문자열 = 미지정(SF 기본 크기/중앙 정렬). 숫자만 입력받는다. */
  width: string;
  height: string;
  positionX: string;
  positionY: string;
  device: PopupDevice;
  /** 화면에서는 항상 "선택된 몰의 집합"으로 다룬다(전체 노출 = 4개 모두 선택). */
  visibleMalls: HomePopupMall[];
  todayCloseEnabled: boolean;
  /** `<input type="datetime-local">` 값("YYYY-MM-DDTHH:mm"). 빈 문자열 = 제약 없음. */
  displayStartAt: string;
  displayEndAt: string;
  isActive: boolean;
  sortOrder: string;
}

export const EMPTY_HOME_POPUP_FORM: HomePopupFormState = {
  popupKey: '',
  title: '',
  contentHtml: '',
  linkUrl: '',
  width: '',
  height: '',
  positionX: '',
  positionY: '',
  device: 'all',
  // 신규 등록 기본값 = 전체 노출(BE 기본 의미와 일치).
  visibleMalls: [...HOME_POPUP_MALLS],
  todayCloseEnabled: true,
  displayStartAt: '',
  displayEndAt: '',
  isActive: true,
  sortOrder: '0',
};

/** ISO-8601 → `<input type="datetime-local">` 값. 로컬 타임존 기준으로 표시한다. */
export const isoToLocalInput = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** `<input type="datetime-local">` 값 → ISO-8601(UTC). 빈 값/파싱 실패는 null(= 제약 없음). */
export const localInputToIso = (local: string): string | null => {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const asDevice = (value: string | null | undefined): PopupDevice =>
  POPUP_DEVICES.includes(value as PopupDevice) ? (value as PopupDevice) : 'all';

/**
 * 와이어 → 폼의 선택 집합. BE 는 null 과 빈 배열을 **둘 다 전체 노출**로 해석하므로
 * (`MallCodes.normalize` + `isVisibleInMall`) 어느 쪽이든 4개 모두 선택된 상태로 되돌린다.
 * 지원하지 않는 코드는 버린다(서버도 제거하므로 남겨두면 화면만 어긋난다).
 */
export const mallsFromWire = (codes: string[] | null | undefined): HomePopupMall[] => {
  if (codes == null || codes.length === 0) return [...HOME_POPUP_MALLS];
  const selected = new Set(codes);
  const known = HOME_POPUP_MALLS.filter((m) => selected.has(m));
  return known.length === 0 ? [...HOME_POPUP_MALLS] : known;
};

/**
 * 폼의 선택 집합 → 와이어. 전체 선택은 **null**(전체 노출의 정본 표현)로 보낸다.
 * 빈 배열도 BE 가 전체 노출로 해석하므로, 운영자가 아무것도 고르지 않은 상태를
 * "전체 노출"로 저장해 버리지 않도록 폼 단계에서 최소 1개를 강제한다(canSubmit).
 */
export const mallsToWire = (selected: ReadonlyArray<HomePopupMall>): string[] | null => {
  const set = new Set(selected);
  const ordered = HOME_POPUP_MALLS.filter((m) => set.has(m));
  return ordered.length === HOME_POPUP_MALLS.length ? null : [...ordered];
};

/** 빈 문자열 = 미지정(null). 숫자가 아니거나 음수면 null 로 떨어뜨린다(BE 는 음수에 400). */
export const parseGeometry = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.trunc(n);
};

const parseSortOrder = (raw: string): number => {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

/** 저장 가능 여부 — BE 의 @NotBlank 3종 + 노출 몰 최소 1개. */
export const canSubmitHomePopup = (form: HomePopupFormState): boolean =>
  form.popupKey.trim() !== '' &&
  form.title.trim() !== '' &&
  form.contentHtml.trim() !== '' &&
  form.visibleMalls.length > 0;

/**
 * 노출 기간이 뒤집혔는지. 시작은 inclusive, 종료는 exclusive 이므로 같은 값이면 노출 구간이
 * 비어 팝업이 한 번도 뜨지 않는다 — 저장은 되지만 운영자가 의도했을 리 없어 경고한다.
 */
export const hasInvalidPeriod = (form: HomePopupFormState): boolean => {
  const start = localInputToIso(form.displayStartAt);
  const end = localInputToIso(form.displayEndAt);
  if (!start || !end) return false;
  return new Date(end).getTime() <= new Date(start).getTime();
};

export const formFromHomePopup = (popup: HomePopup): HomePopupFormState => ({
  popupKey: popup.popupKey,
  title: popup.title,
  contentHtml: popup.contentHtml,
  linkUrl: popup.linkUrl ?? '',
  width: popup.width == null ? '' : String(popup.width),
  height: popup.height == null ? '' : String(popup.height),
  positionX: popup.positionX == null ? '' : String(popup.positionX),
  positionY: popup.positionY == null ? '' : String(popup.positionY),
  device: asDevice(popup.device),
  visibleMalls: mallsFromWire(popup.visibleMallCodes),
  todayCloseEnabled: popup.todayCloseEnabled,
  displayStartAt: isoToLocalInput(popup.displayStartAt),
  displayEndAt: isoToLocalInput(popup.displayEndAt),
  isActive: popup.isActive,
  sortOrder: String(popup.sortOrder),
});

/**
 * 폼 → 요청 본문.
 *
 * `popupKey` 는 수정 시 서버가 무시하지만 @NotBlank 검증은 그대로 걸리므로 항상 담아 보낸다
 * (빼면 수정이 400 으로 떨어진다). 폼에서 읽기 전용으로 두는 것으로 값 변경을 막는다.
 */
export const buildHomePopupPayload = (form: HomePopupFormState): HomePopupPayload => ({
  popupKey: form.popupKey.trim(),
  title: form.title.trim(),
  contentHtml: form.contentHtml,
  linkUrl: form.linkUrl.trim() === '' ? null : form.linkUrl.trim(),
  width: parseGeometry(form.width),
  height: parseGeometry(form.height),
  positionX: parseGeometry(form.positionX),
  positionY: parseGeometry(form.positionY),
  device: form.device,
  visibleMallCodes: mallsToWire(form.visibleMalls),
  todayCloseEnabled: form.todayCloseEnabled,
  displayStartAt: localInputToIso(form.displayStartAt),
  displayEndAt: localInputToIso(form.displayEndAt),
  isActive: form.isActive,
  sortOrder: parseSortOrder(form.sortOrder),
});
